import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import apiClient from "api/axiosConfig";

const getSingleValue = (options, value) => {
  if (value === "" || value === null || value === undefined) return null;
  return options.find((opt) => String(opt.value) === String(value)) || null;
};

const endpointCache = {};
const pendingRequests = {};

const cacheEmitter = new EventTarget();

const customSelectStyles = (isInvalid = false) => ({
  control: (base) => ({
    ...base,
    minHeight: "36px",
    fontSize: "12px",
    borderColor: isInvalid ? "#dc3545 !important" : base.borderColor,
    "&:hover": {
      borderColor: isInvalid ? "#dc3545 !important" : base.borderColor,
    }
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: "12px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    minWidth: "100%",
    borderRadius: "6px"
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: "200px",
    padding: "4px"
  }),
  option: (base, state) => ({
    ...base,
    fontSize: "11.5px",
    padding: "7px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isSelected ? "#0d6efd" : state.isFocused ? "#f0f4f8" : "transparent",
    color: state.isSelected ? "#fff" : "#212529"
  })
});

const LazyLoadSelectField = ({
  label,
  value,
  endpoint,
  onChange,
  isRequired = false,
  hasError = false,
  placeholder = "Select...",
  className = "",
  style = {},
}) => {
  const [options, setOptions] = useState(() => endpointCache[endpoint]?.options || []);
  const [page, setPage] = useState(() => endpointCache[endpoint]?.page || 1);
  const [hasMore, setHasMore] = useState(() => endpointCache[endpoint]?.hasMore ?? true);
  const [isLoading, setIsLoading] = useState(false);

  const fetchOptions = useCallback(
    async (startPage) => {
      const reqKey = `${endpoint}_${startPage}`;
      if (pendingRequests[reqKey]) return;

      setIsLoading(true);
      pendingRequests[reqKey] = (async () => {
        try {
          const [res1, res2] = await Promise.all([
            apiClient.get(`${endpoint}?page=${startPage}`),
            apiClient.get(`${endpoint}?page=${startPage + 1}`),
          ]);

          const items1 = res1.data?.data || res1.data || [];
          const items2 = res2.data?.data || res2.data || [];
          const combined = [...items1, ...items2];

          const formatted = combined.map((item) => ({
            value: item.id !== undefined ? Number(item.id) : (item.code !== undefined ? item.code : item.value),
            label: item.name || item.language_name || item.domain || item.host || item.label || `ID ${item.id}`,
          }));

          const updatedOptions = startPage === 1 
            ? formatted 
            : [...(endpointCache[endpoint]?.options || []), ...formatted];
          const updatedPage = startPage + 1;
          const updatedHasMore = items2.length >= 10;

          endpointCache[endpoint] = {
            options: updatedOptions,
            page: updatedPage,
            hasMore: updatedHasMore,
          };

          setOptions(updatedOptions);
          setPage(updatedPage);
          setHasMore(updatedHasMore);

          cacheEmitter.dispatchEvent(new CustomEvent(endpoint, { detail: endpointCache[endpoint] }));
        } catch (err) {
          console.error(`Error fetching options from ${endpoint}:`, err);
        } finally {
          setIsLoading(false);
          delete pendingRequests[reqKey];
        }
      })();
    },
    [endpoint]
  );

  useEffect(() => {
    const handleCacheUpdate = (e) => {
      if (e.detail) {
        setOptions(e.detail.options);
        setPage(e.detail.page);
        setHasMore(e.detail.hasMore);
      }
    };

    cacheEmitter.addEventListener(endpoint, handleCacheUpdate);

    if (!endpointCache[endpoint]) {
      fetchOptions(1);
    }

    return () => {
      cacheEmitter.removeEventListener(endpoint, handleCacheUpdate);
    };
  }, [endpoint, fetchOptions]);

  const handleScrollBottom = () => {
    if (hasMore && !isLoading) {
      fetchOptions(page + 1);
    }
  };

  return (
    <div className={className} style={style}>
      <label className="form-label small fw-bold mb-1">
        {label} {isRequired && <span className="text-danger">*</span>}
      </label>
      <Select
        options={options}
        value={getSingleValue(options, value)}
        onChange={(opt) => onChange(opt ? opt.value : "")}
        onMenuScrollToBottom={handleScrollBottom}
        isLoading={isLoading}
        placeholder={placeholder}
        styles={customSelectStyles(hasError)}
        classNamePrefix="react-select"
        className={hasError ? "is-invalid" : ""}
        menuPlacement="auto"
        menuPortalTarget={document.body}
      />
    </div>
  );
};

LazyLoadSelectField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  endpoint: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  isRequired: PropTypes.bool,
  hasError: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default React.memo(LazyLoadSelectField);